import { CHECKOUT_ITEM_TYPE_FLIGHT } from 'constants/checkout'
import uuidV4 from 'lib/string/uuidV4Utils'
import { isRewardFareClass, FlightSegment, type FareClassWithReward, type FlightsClassTypes, FlightViewTypes, toBaseFareClass } from 'constants/flight'
import { last } from 'lib/array/arrayUtils'
import { getEffectiveFlightPrice, getTotalStopCount, isCarryOnBaggageIncluded, isCheckedBaggageIncluded, isFnfPriceActive } from 'lib/flights/flightUtils'
import { getFareFamilyBaggageInclusion } from 'lib/flights/flightTileBaggage'
import { countOccupantsForFlights } from 'lib/offer/occupancyUtils'
import { generatePassengersFromOccupants } from 'checkout/lib/utils/flights/passenger'
import { CHARTER_FLIGHT_DETAILS } from 'constants/charterFlight'
import { CHARTER } from 'constants/provider'
import moment from 'moment'
import { getBudgetedFeederLegFare } from 'lib/flights/feederBundleLabels'

/**
 * Returns the appropriate expiry timestamp for a flight.
 * Charter flights use departure minus 2 days as expiry (minimum lead time for booking),
 * which is far enough in the future that no countdown timer is shown.
 * Non-charter flights use the provided search-based expiry.
 */
export function getFlightExpiry(journey: App.JourneyV2, searchExpiry: number): number {
  if (journey.provider === CHARTER) {
    return moment(journey.flightGroup.flights[0].departureDateTime).subtract(2, 'days').toDate().getTime()
  }
  return searchExpiry
}

export function getJourneyFields(
  journey: App.JourneyV2,
  requiredSeats?: number,
  fareFamily?: App.FlightFareFamily,
) {
  const effectivePrice = getEffectiveFlightPrice(journey, requiredSeats)
  const fareFamilyBaggage = getFareFamilyBaggageInclusion(fareFamily)
  return {
    journeyId: journey.id,
    journeyKey: journey.bookingInfo.journeyKey,
    // Rich-format key for svc-lux-loyalty availability + svc-order Phase-0 check.
    rewardJourneyKey: journey.journeyKey,
    provider: journey.provider,
    providerSearchId: journey.bookingInfo.providerSearchId,
    cost: effectivePrice.all.totalFare,
    carrierImage: {
      url: journey.carrierLogo,
      title: journey.carrierName,
    },
    hasIncludedCheckedBaggage: fareFamilyBaggage.checked ?? isCheckedBaggageIncluded(journey),
    hasIncludedCarryOnBaggage: fareFamilyBaggage.carryOn ?? isCarryOnBaggageIncluded(journey),
    paymentScheduleSupported: journey.paymentScheduleSupported,
  }
}

function getViewType(
  departing?: App.AnyJourney,
  returning?: App.AnyJourney,
  viewType?: string,
) {
  const { RETURN, TWO_ONE_WAYS_AND_RETURN } = FlightViewTypes
  const isReturnCombinedFlight = departing?.fareType === RETURN && returning?.fareType === RETURN
  return isReturnCombinedFlight ? TWO_ONE_WAYS_AND_RETURN : viewType
}

interface FlightItemUpdate {
  originAirportCode?: string
  destinationAirportCode?: string
  searchId?: string
  forceBundleId?: string
}

export const EmptyBaggage: App.Checkout.FlightItemPaxBaggageMap = { baggage: {}, carryOnBaggage: {} }

/**
 * Snaps a feeder leg's fare to its FDA-protected price: when the leg has its own
 * budget/buffer, the customer is charged/shown `budget` if the fare sits within
 * `budget + buffer`, otherwise the real fare. When the leg has no budget of its own,
 * the bundle covers it via the other leg's allocation instead (see `getPendingReturningLegPrice`
 * / `getBudgetedSelectedDepartingLegPrice` in `checkout/selectors/view/flights`), so this
 * leg's own contribution is just the excess above that other allocation (0 within its
 * band). Reused by the tile/modal previews so they can never drift from the real charge.
 */
export function applyFeederBudgetToCost(
  cost: number,
  bundleBudget: App.Checkout.FeederBundleBudget,
  segment: 'departing' | 'returning',
): number {
  const ownBudget = segment === FlightSegment.DEPARTING ? bundleBudget.departingLegBudget : bundleBudget.returningLegBudget
  const ownBuffer = (segment === FlightSegment.DEPARTING ? bundleBudget.departingLegBuffer : bundleBudget.returningLegBuffer) ?? 0
  if (ownBudget && ownBudget > 0) {
    // Leg has its own budget allocation: bump to base when fare sits in the budget+buffer band
    // so the summary reflects what the bundle pays for this leg.
    return getBudgetedFeederLegFare(cost, ownBudget, ownBuffer)
  }
  // No own budget — bundle covers this leg via the other leg's allocation (the tile/modal
  // labels render the displayed delta using `resolveDepartingFeederReference`). The cart
  // contribution for this leg is the same delta: 0 within the fallback band, excess above it.
  const otherBudget = segment === FlightSegment.DEPARTING ? bundleBudget.returningLegBudget : bundleBudget.departingLegBudget
  const otherBuffer = (segment === FlightSegment.DEPARTING ? bundleBudget.returningLegBuffer : bundleBudget.departingLegBuffer) ?? 0
  if (!otherBudget || otherBudget <= 0) return cost
  if (cost <= otherBudget + otherBuffer) return 0
  return cost - otherBudget
}

export function setFlightCheckoutItemJourney(
  checkoutItem: App.Checkout.FlightItem,
  journey: App.JourneyV2,
  segment: 'departing' | 'returning',
  expiry: number,
  options: {
    fareFamily?: App.FlightFareFamily
    baggage?: App.Checkout.FlightItemPaxBaggageMap
    forceBundleId?: string
    selectedCabinClass?: FlightsClassTypes
    areAllFlightsCovered?: boolean
    /**
     * Cheapest available fare for the *other*, not-yet-selected leg of a two-one-ways
     * bundle - only meaningful when `segment` is DEPARTING. Persisted onto the item so
     * the cart/order summary can estimate the package total honestly
     * (`getPendingReturningLegPrice`) instead of falling back to `quotedFare / 2`.
     */
    cheapestReturningPrice?: number
  },
) {
  const requiredSeats = checkoutItem.passengers.filter(passenger => passenger.type !== 'infant').length
  const journeyFields = getJourneyFields(journey, requiredSeats, options.fareFamily)
  const effectivePrice = getEffectiveFlightPrice(journey, requiredSeats)

  // FLY-5300: pre-select local override. When this leg is inside the FDA base+buffer
  // band, override the cost so the summary updates match the displayed `+A$X` tile delta
  // before /select returns the server-authoritative price. When a leg has no own budget,
  // the bundle covers it via the other leg's allocation; contribute only the excess so
  // selecting an in-budget option doesn't move the summary at all.
  const bundleBudget = checkoutItem.bundleBudget
  if (bundleBudget && journeyFields.cost !== undefined) {
    journeyFields.cost = applyFeederBudgetToCost(journeyFields.cost, bundleBudget, segment)
  }
  const isFnfApplied = isFnfPriceActive(journey, requiredSeats)
  const isJourneyPromotionApplied = !isFnfApplied && !!journey.promotionPrice
  const promotionCost = !isFnfApplied ? options.fareFamily?.promotion : undefined
  let syncedFareFamily = (isFnfApplied || isJourneyPromotionApplied || promotionCost) && options.fareFamily ? {
    ...options.fareFamily,
    cost: isJourneyPromotionApplied || isFnfApplied ? {
      ...options.fareFamily.cost,
      adult: effectivePrice.adult.totalFare ?? 0,
      children: effectivePrice.child?.totalFare ?? 0,
      infant: effectivePrice.infant?.totalFare ?? 0,
      all: effectivePrice.all.totalFare,
    } : {
      ...options.fareFamily.cost,
      ...promotionCost,
    },
  } : options.fareFamily

  // Mirror the leg budget override on `fareFamily.cost.all` so the checkout summary, which
  // prefers `flight.fareFamily.cost.all` over `flight.cost`, also reflects the budgeted price.
  if (bundleBudget && syncedFareFamily) {
    const originalAll = syncedFareFamily.cost.all ?? 0
    const budgetedAll = applyFeederBudgetToCost(originalAll, bundleBudget, segment)
    if (budgetedAll !== originalAll) {
      syncedFareFamily = {
        ...syncedFareFamily,
        cost: { ...syncedFareFamily.cost, all: budgetedAll },
      }
    }
  }

  const flights = [FlightSegment.DEPARTING, FlightSegment.RETURNING]
    .map((currentSegment): App.Checkout.FlightItemFlight | undefined => {
      const flight = currentSegment === FlightSegment.DEPARTING ? checkoutItem.flights[0] : checkoutItem.flights[1]

      if (segment === currentSegment) {
        const firstFlight = journey.flightGroup.flights[0]
        const lastFlight = last(journey.flightGroup.flights)

        return {
          ...journeyFields,
          rewardJourneyKey: syncedFareFamily?.journeyKey ?? journeyFields.rewardJourneyKey,
          cost: syncedFareFamily?.cost.all ?? journeyFields.cost,
          carrier: firstFlight?.marketingAirline.code,
          extras: options.baggage ?? EmptyBaggage,
          fareFamily: syncedFareFamily,
          departingAirportCode: firstFlight?.departureAirport,
          arrivalAirportCode: lastFlight?.arrivalAirport,
          departingDate: firstFlight?.departingDate,
          arrivalAirportName: lastFlight?.arrivalAirportName,
          departingAirportName: firstFlight?.departureAirportName,
          arrivalDateTime: lastFlight?.arrivalDateTime,
          departureDateTime: firstFlight?.departureDateTime,
          duration: journey.flightGroup.totalFlightDuration,
          numberOfStops: getTotalStopCount(journey.flightGroup.flights),
          layoverDuration: journey.flightGroup.totalStopOverTime,
          ...(options.areAllFlightsCovered ? { withEmbeddedInsurance: true } : {}),
        }
      }

      if (segment === FlightSegment.DEPARTING && currentSegment === FlightSegment.RETURNING) {
        return undefined
      }

      if (options.areAllFlightsCovered && flight) {
        flight.withEmbeddedInsurance = true
      }

      return flight
    })
    .filter((flight): flight is App.Checkout.FlightItemFlight => flight !== undefined)

  const updatedItem: App.Checkout.FlightItem = {
    ...checkoutItem,
    selectedCabinClass: options.selectedCabinClass,
    expiry,
    // our flight is changing, reset the fare fetching behaviour
    hasFinalFare: false,
    fareFetchError: undefined,
    journeyType: journey.itemType,
    forceBundleId: options.forceBundleId ?? checkoutItem.forceBundleId,
    totalFare: 0,
    searchId: checkoutItem.searchId,
    viewType: journey.fareType === FlightViewTypes.RETURN ? FlightViewTypes.TWO_ONE_WAYS_AND_RETURN : checkoutItem.viewType,
    paymentScheduleSupported: journey.paymentScheduleSupported ?? checkoutItem.paymentScheduleSupported,
    flights,
    // Only meaningful while the returning leg is still unselected; once it's set, its
    // own real fare is known, so drop the placeholder rather than let it linger unused.
    pendingReturningLegFarePrice: segment === FlightSegment.DEPARTING ? options.cheapestReturningPrice : undefined,
  }

  return updatedItem
}

export function resetFlightCheckoutItem(
  checkoutItem: App.Checkout.FlightItem,
) {
  const updatedItem: App.Checkout.FlightItem = {
    ...checkoutItem,
    flights: [],
  }

  return updatedItem
}

export function setFlightCheckoutItemJourneyFareFamily(
  checkoutItem: App.Checkout.FlightItem,
  flightIndex: number,
  options: {
    fareFamily?: App.FlightFareFamily
  },
) {
  const updatedItem: App.Checkout.FlightItem = {
    ...checkoutItem,
    flights: checkoutItem.flights.map((flight, index) => {
      if (index === flightIndex) {
        return {
          ...flight,
          fareFamily: options.fareFamily,
        }
      }

      return flight
    }),
  }

  return updatedItem
}

export function updateFlightCheckoutItem(
  checkoutItem: App.Checkout.FlightItem,
  updates: FlightItemUpdate,
): App.Checkout.FlightItem {
  const {
    originAirportCode,
    destinationAirportCode,
    searchId,
    forceBundleId,
  } = updates

  const flights = searchId !== checkoutItem.searchId ? [] : checkoutItem.flights

  const updatedItem: App.Checkout.FlightItem = {
    ...checkoutItem,
    originAirportCode: originAirportCode ?? checkoutItem.originAirportCode,
    destinationAirportCode: destinationAirportCode ?? checkoutItem.destinationAirportCode,
    searchId,
    forceBundleId,
    // we just updated the item, fare may have changed
    hasFinalFare: false,
    fareFetchError: undefined,
    flights,
  }

  return updatedItem
}

interface GenerateItemFields {
  originAirportCode: string
  destinationAirportCode?: string
  occupants: App.Occupants
  passengers: Array<App.Checkout.FlightPassenger>
  fareType: 'return' | 'oneWay' | 'multiCity'
  viewType: string
  searchId?: string
  isFlightCredit?: boolean
  itemId?: string
  fareFamilies?: Array<App.FlightFareFamily | undefined>
  baggage?: Array<App.Checkout.FlightItemPaxBaggageMap>
  flights?: Array<App.JourneyV2>
  bundledItemIds?: Array<string>
  quotedFare?: number
  expiry?: number
  selectedCabinClass?: FlightsClassTypes
  areAllFlightsCovered?: boolean
  reservationId?: string
  isFlightDateChange?: boolean
  charterBundleRoute?: App.CharterBundleRoute
  isFnFBooking?: boolean
  /**
   * Feeder bundle budget propagated from the offer page. Set on the FEEDER flight
   * item only (not the charter item) for non-MEL Maldives bundle flows.
   */
  bundleBudget?: App.Checkout.FeederBundleBudget
  isPromotionPriceApplied?: boolean
  isClassicReward?: boolean
}

export function generateFlightCheckoutItem(fields: GenerateItemFields): App.Checkout.FlightItem {
  const {
    searchId,
    flights = [],
    baggage = [],
    fareFamilies = [],
    occupants,
    passengers,
    fareType,
    viewType,
    isFlightCredit,
    itemId,
    bundledItemIds,
    originAirportCode,
    destinationAirportCode,
    quotedFare,
    expiry,
    selectedCabinClass,
    areAllFlightsCovered,
    reservationId,
    isFlightDateChange,
    charterBundleRoute,
    isFnFBooking,
    bundleBudget,
    isPromotionPriceApplied,
    isClassicReward,
  } = fields
  return {
    itemId: itemId ?? uuidV4(),
    transactionKey: uuidV4(),
    carrier: flights[0]?.flightGroup?.flights[0]?.marketingAirline.code,
    itemType: CHECKOUT_ITEM_TYPE_FLIGHT,
    originAirportCode,
    destinationAirportCode: destinationAirportCode!,
    passengers,
    occupants,
    fareType,
    viewType: getViewType(flights[0], flights[1], viewType),
    quotedFare,
    journeyType: flights[0]?.itemType,
    isFlightCredit: !!isFlightCredit,
    isFnFBooking: isFnFBooking ?? flights.some(flight => isFnfPriceActive(
      flight,
      passengers.filter(passenger => passenger.type !== 'infant').length,
    )),
    fnfPromoCodeApplicable: flights.length > 0 && flights.every(flight =>
      flight.flightGroup.flights.some(f => f.fnfPromoCodeApplicable),
    ),
    searchId,
    reservationId,
    bundledItemIds,
    expiry,
    selectedCabinClass,
    charterBundleRoute,
    isPromotionPriceApplied,
    isClassicReward,
    flights: flights.map((flight, index) => {
      const journey = getJourneyFields(flight, undefined, fareFamilies[index])
      return {
        carrier: flight.flightGroup.flights[0].marketingAirline.code,
        journeyId: journey.journeyId,
        journeyKey: journey.journeyKey,
        rewardJourneyKey: fareFamilies[index]?.journeyKey ?? journey.rewardJourneyKey,
        provider: journey.provider,
        providerSearchId: journey.providerSearchId,
        fareFamily: fareFamilies[index],
        extras: baggage[index] ?? EmptyBaggage,
        cost: fareFamilies[index]?.cost.all ?? journey.cost,
        carrierImage: journey.carrierImage,
        departingAirportCode: flight.flightGroup.flights[0].departureAirport,
        arrivalAirportCode: last(flight.flightGroup.flights).arrivalAirport,
        departingDate: flight.flightGroup.flights[0].departingDate,
        arrivalAirportName: last(flight.flightGroup.flights).arrivalAirportName,
        departingAirportName: flight.flightGroup.flights[0].departureAirportName,
        arrivalDateTime: last(flight.flightGroup.flights).arrivalDateTime,
        departureDateTime: flight.flightGroup.flights[0].departureDateTime,
        duration: flight.flightGroup.totalFlightDuration,
        numberOfStops: getTotalStopCount(flight.flightGroup.flights),
        hasIncludedCheckedBaggage: journey.hasIncludedCheckedBaggage,
        hasIncludedCarryOnBaggage: journey.hasIncludedCarryOnBaggage,
        paymentScheduleSupported: flight.paymentScheduleSupported,
        layoverDuration: flight.flightGroup.totalStopOverTime,
        ...(areAllFlightsCovered ? { withEmbeddedInsurance: true } : {}),
      }
    }),
    hasFinalFare: false,
    isFlightDateChange,
    paymentScheduleSupported: flights[0]?.paymentScheduleSupported ?? false,
    bundleBudget,
  }
}

export function generateAccommodationBundleFlightItems({
  rooms,
  airport,
  offer,
  accommodationItems,
  quotedFare,
  charterBundleRoute,
  fareClass,
  departingCharterFlightJourney,
  returningCharterFlightJourney,
  departingCharterFareFamily,
  returningCharterFareFamily,
  charterFlightSearchResultsTimestamp,
  charterFlightSearchId,
}: {
  rooms: Array<App.RoomOccupants>
  airport: App.Airport
  offer: App.HotelOffer | App.HotelOfferSummary | App.VillaOffer | App.VillaOfferSummary
  accommodationItems: Array<App.Checkout.InstantBookingLEHotelItem>
  quotedFare: number | undefined
  charterBundleRoute?: App.CharterBundleRoute
  fareClass?: FareClassWithReward
  departingCharterFlightJourney?: App.JourneyV2
  returningCharterFlightJourney?: App.JourneyV2
  departingCharterFareFamily?: App.FlightFareFamily
  returningCharterFareFamily?: App.FlightFareFamily
  charterFlightSearchResultsTimestamp?: number
  charterFlightSearchId?: string
  charterFlightQuotedFare?: number
  charterFeederFlightQuotedFare?: number
}): Array<App.Checkout.FlightItem> {
  const flightItems: Array<App.Checkout.FlightItem> = []
  const flightOccupants = countOccupantsForFlights(rooms)
  const charterBundleRouteInfo = charterBundleRoute ? CHARTER_FLIGHT_DETAILS[charterBundleRoute] : undefined

  if (charterBundleRouteInfo && departingCharterFlightJourney && returningCharterFlightJourney && charterFlightSearchResultsTimestamp && charterFlightSearchId && departingCharterFareFamily && returningCharterFareFamily) {
    const seatEligiblePassengers = flightOccupants.adults + flightOccupants.children + flightOccupants.teenagers
    const isFnfApplied =
      isFnfPriceActive(departingCharterFlightJourney, seatEligiblePassengers) &&
      isFnfPriceActive(returningCharterFlightJourney, seatEligiblePassengers)
    const isPromotionPriceApplied = !isFnfApplied && !!(
      departingCharterFlightJourney.promotionPrice ||
      returningCharterFlightJourney.promotionPrice ||
      departingCharterFareFamily.promotion ||
      returningCharterFareFamily.promotion
    )
    const charterFlightPrice = isFnfApplied
      ? getEffectiveFlightPrice(departingCharterFlightJourney, seatEligiblePassengers).all.totalFare +
      getEffectiveFlightPrice(returningCharterFlightJourney, seatEligiblePassengers).all.totalFare
      : (departingCharterFareFamily.promotion?.all ?? departingCharterFareFamily.cost.all) +
        (returningCharterFareFamily.promotion?.all ?? returningCharterFareFamily.cost.all)

    const charterSearchExpiry = getFlightExpiry(departingCharterFlightJourney, 0)

    // Charter flight
    const baseCharterFlightItem = generateFlightCheckoutItem({
      originAirportCode: charterBundleRouteInfo.originAirport.airportCode,
      destinationAirportCode: charterBundleRouteInfo.destinationAirport.airportCode,
      bundledItemIds: accommodationItems.map(item => item.itemId),
      occupants: flightOccupants,
      fareType: 'return',
      viewType: FlightViewTypes.TWO_ONE_WAYS,
      passengers: generatePassengersFromOccupants(flightOccupants),
      quotedFare: charterFlightPrice,
      searchId: charterFlightSearchId,
      charterBundleRoute,
      isFnFBooking: isFnfApplied,
      isPromotionPriceApplied,
    })

    // Persist the base cabin class for cart-svc validation; signal "reward
    // booking" via the dedicated `isClassicReward` flag instead.
    const baseCabin = toBaseFareClass(fareClass as FareClassWithReward)
    const isClassicReward = isRewardFareClass(fareClass as FareClassWithReward)

    const departingCharterFlightItem = setFlightCheckoutItemJourney(
      baseCharterFlightItem,
      departingCharterFlightJourney,
      'departing',
      charterSearchExpiry,
      {
        forceBundleId: offer.forceBundleId,
        selectedCabinClass: baseCabin,
        fareFamily: departingCharterFareFamily,
      },
    )

    const charterFlightItem: App.Checkout.FlightItem = {
      ...setFlightCheckoutItemJourney(
        departingCharterFlightItem,
        returningCharterFlightJourney,
        'returning',
        charterSearchExpiry,
        {
          forceBundleId: offer.forceBundleId,
          selectedCabinClass: baseCabin,
          fareFamily: returningCharterFareFamily,
        },
      ),
      isClassicReward,
    }

    flightItems.push(charterFlightItem)

    if (airport.code !== charterBundleRouteInfo.originAirport.airportCode) {
      // Feeder flight bundleBudget: derived from feeder prices carried on the charter
      // journeys (svc-flights attaches them when feederOriginAirportCode is set).
      // Values are aggregate across paying pax already. The base budget feeds the quote;
      // buffer only affects relative display and the local pre-select override.
      const departingLegBudget = departingCharterFlightJourney.feederEstimatedPrice ?? 0
      const returningLegBudget = returningCharterFlightJourney.feederEstimatedPrice ?? 0
      const departingLegBuffer = departingCharterFlightJourney.feederBufferPrice ?? 0
      const returningLegBuffer = returningCharterFlightJourney.feederBufferPrice ?? 0
      const bundleBudget: App.Checkout.FeederBundleBudget | undefined =
        departingLegBudget > 0 || returningLegBudget > 0 || departingLegBuffer > 0 || returningLegBuffer > 0
          ? {
              tripBudget: departingLegBudget + returningLegBudget,
              departingLegBudget,
              returningLegBudget,
              tripBuffer: departingLegBuffer + returningLegBuffer,
              departingLegBuffer,
              returningLegBuffer,
            }
          : undefined

      // Feeder flight
      flightItems.push(generateFlightCheckoutItem({
        originAirportCode: airport.code,
        destinationAirportCode: charterBundleRouteInfo.originAirport.airportCode,
        bundledItemIds: accommodationItems.map(item => item.itemId),
        occupants: flightOccupants,
        fareType: 'return',
        viewType: FlightViewTypes.TWO_ONE_WAYS,
        passengers: generatePassengersFromOccupants(flightOccupants),
        quotedFare: quotedFare ? quotedFare - charterFlightPrice : undefined,
        bundleBudget,
      }))
    }
  } else {
    // Normal flight
    flightItems.push(generateFlightCheckoutItem({
      originAirportCode: airport.code,
      destinationAirportCode: offer.flightDestinationPort,
      bundledItemIds: accommodationItems.map(item => item.itemId),
      occupants: flightOccupants,
      fareType: 'return',
      viewType: FlightViewTypes.TWO_ONE_WAYS,
      passengers: generatePassengersFromOccupants(flightOccupants),
      quotedFare,
    }))
  }

  return flightItems
}
